import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

const COLORS = [
  { label: 'Bleu',   value: 'BLUE',   hex: '#185FA5' },
  { label: 'Violet', value: 'PURPLE', hex: '#534AB7' },
  { label: 'Vert',   value: 'GREEN',  hex: '#276749' },
  { label: 'Orange', value: 'ORANGE', hex: '#C05621' },
  { label: 'Rouge',  value: 'RED',    hex: '#9B2C2C' },
];

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes slideIn {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  input:focus {
    border-color: #667eea !important;
    box-shadow: 0 0 0 3px rgba(102,126,234,0.12) !important;
    outline: none;
  }
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
  input[type=number] { -moz-appearance: textfield; }
  button:active:not(:disabled) { transform: scale(0.98) !important; }
`;
document.head.appendChild(styleSheet);

async function sendCommand(tableId, command) {
  await addDoc(collection(db, 'esp'), {
    table: parseInt(tableId),
    command,
    processed: false,
    createdAt: serverTimestamp(),
  });
}

export default function PomodoroSetup() {
  const { id: tableId } = useParams();

  const [studyMin,  setStudyMin]  = useState(25);
  const [breakMin,  setBreakMin]  = useState(5);
  const [slots,     setSlots]     = useState(4);
  const [autoBreak, setAutoBreak] = useState(false);
  const [color,     setColor]     = useState('BLUE');
  const [loading,   setLoading]   = useState(false);
  const [message,   setMessage]   = useState('');
  const [msgType,   setMsgType]   = useState('');
  const [sent,      setSent]      = useState(false);

  async function cmd(command) {
    try { await sendCommand(tableId, command); }
    catch(e) { console.error(e); }
  }

  async function handleStart() {
    if (!studyMin || !breakMin || !slots) {
      setMessage('Veuillez remplir tous les champs.');
      setMsgType('error');
      return;
    }
    setLoading(true);
    setMessage('');
    const auto = autoBreak ? 'AUTO' : 'MANUAL';
    try {
      await cmd(`START:${studyMin}:${breakMin}:${slots}:${auto}:${color}`);
      setMessage(`Session lancée sur l'écran de la table ${tableId} !`);
      setMsgType('success');
      setSent(true);
    } catch(e) {
      setMessage('Une erreur s\'est produite. Veuillez réessayer.');
      setMsgType('error');
    }
    setLoading(false);
  }

  async function handleColorChange(c) {
    setColor(c);
    if (sent) await cmd(`COLOR:${c}`);
  }

  async function handlePause()  { setLoading(true); await cmd('PAUSE');  setLoading(false); setMessage('Pause envoyée.'); setMsgType('info'); }
  async function handleResume() { setLoading(true); await cmd('RESUME'); setLoading(false); setMessage('Reprise envoyée.'); setMsgType('info'); }
  async function handleStop()   {
    setLoading(true);
    await cmd('STOP');
    setLoading(false);
    setSent(false);
    setMessage('Session arrêtée.');
    setMsgType('error');
    setTimeout(() => setMessage(''), 2000);
  }

  const totalMin = studyMin * slots + breakMin * Math.max(0, slots - 1);
  const selectedColor = COLORS.find(c => c.value === color);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: 16,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    }}>
      <div style={{
        background: 'white', borderRadius: 20, padding: '32px 24px',
        maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 56, marginBottom: 10 }}>⏱</div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#1a202c', margin: 0 }}>
            Pomodoro
          </h1>
          <p style={{ color: '#718096', fontSize: 14, margin: '6px 0 0', fontWeight: 500 }}>
            Table #{tableId}
          </p>
        </div>

        {!sent ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>

              {[
                { label: 'Durée de travail', sub: 'minutes par session', val: studyMin, set: setStudyMin, min: 1, max: 120 },
                { label: 'Durée de pause',   sub: 'minutes entre sessions', val: breakMin, set: setBreakMin, min: 1, max: 60 },
                { label: 'Nombre de sessions', sub: 'cycles de travail', val: slots, set: setSlots, min: 1, max: 10 },
              ].map(({ label, sub, val, set, min, max }) => (
                <div key={label}>
                  <label style={{ fontSize: 15, fontWeight: 600, color: '#2d3748', display: 'block', marginBottom: 4 }}>
                    {label}
                  </label>
                  <p style={{ fontSize: 12, color: '#a0aec0', margin: '0 0 8px' }}>{sub}</p>
                  <input
                    type="number" min={min} max={max} value={val}
                    onChange={e => set(Math.max(min, parseInt(e.target.value) || min))}
                    style={{
                      width: '100%', padding: 14, fontSize: 18, fontWeight: 600,
                      border: '2px solid #e2e8f0', borderRadius: 12,
                      color: '#1a202c', background: 'white',
                      boxSizing: 'border-box', transition: 'all 0.2s',
                    }}
                  />
                </div>
              ))}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#2d3748' }}>Pause automatique</div>
                  <div style={{ fontSize: 12, color: '#a0aec0', marginTop: 2 }}>passe en pause sans confirmation</div>
                </div>
                <div onClick={() => setAutoBreak(a => !a)} style={{
                  width: 44, height: 24, borderRadius: 24, cursor: 'pointer', flexShrink: 0,
                  background: autoBreak ? '#667eea' : '#cbd5e0',
                  position: 'relative', transition: 'background 0.2s',
                }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', background: 'white',
                    position: 'absolute', top: 3, left: autoBreak ? 23 : 3, transition: 'left 0.2s',
                  }} />
                </div>
              </div>

              <div>
                <label style={{ fontSize: 15, fontWeight: 600, color: '#2d3748', display: 'block', marginBottom: 10 }}>
                  Couleur de l'écran
                </label>
                <div style={{ display: 'flex', gap: 12 }}>
                  {COLORS.map(c => (
                    <div key={c.value} onClick={() => handleColorChange(c.value)} title={c.label} style={{
                      width: 32, height: 32, borderRadius: '50%', background: c.hex, cursor: 'pointer',
                      outline: color === c.value ? `3px solid ${c.hex}` : '3px solid transparent',
                      outlineOffset: 2, transition: 'outline 0.15s',
                    }} />
                  ))}
                </div>
              </div>
            </div>

            <div style={{
              background: '#f7fafc', borderRadius: 12, padding: '12px 16px',
              display: 'flex', justifyContent: 'space-around', marginBottom: 20,
            }}>
              {[
                { label: 'Travail', val: `${studyMin} min` },
                { label: 'Pause',   val: `${breakMin} min` },
                { label: 'Sessions',val: `${slots}x` },
                { label: 'Total',   val: `${totalMin} min` },
              ].map(({ label, val }) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#a0aec0', fontWeight: 500 }}>{label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#2d3748', marginTop: 2 }}>{val}</div>
                </div>
              ))}
            </div>

            <button onClick={handleStart} disabled={loading} style={{
              width: '100%', padding: 16,
              background: loading ? '#a0aec0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white', border: 'none', borderRadius: 12,
              fontSize: 17, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(102,126,234,0.4)',
              transition: 'all 0.2s',
            }}>
              {loading
                ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                    Envoi en cours...
                  </span>
                : 'Lancer sur l\'écran'}
            </button>
          </>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 13, color: '#718096', marginBottom: 16 }}>
                {studyMin} min travail · {breakMin} min pause · {slots} sessions · {totalMin} min total
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: selectedColor?.hex }} />
                <span style={{ fontSize: 13, color: '#718096' }}>{selectedColor?.label}</span>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 15, fontWeight: 600, color: '#2d3748', display: 'block', marginBottom: 10 }}>
                Changer la couleur
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                {COLORS.map(c => (
                  <div key={c.value} onClick={() => handleColorChange(c.value)} title={c.label} style={{
                    width: 32, height: 32, borderRadius: '50%', background: c.hex, cursor: 'pointer',
                    outline: color === c.value ? `3px solid ${c.hex}` : '3px solid transparent',
                    outlineOffset: 2, transition: 'outline 0.15s',
                  }} />
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              <button onClick={handlePause} disabled={loading} style={{
                padding: '12px 6px', borderRadius: 12, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', border: '2px solid #e2e8f0',
                background: 'white', color: '#2d3748',
              }}>Pause</button>
              <button onClick={handleResume} disabled={loading} style={{
                padding: '12px 6px', borderRadius: 12, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', border: 'none',
                background: 'linear-gradient(135deg, #48bb78, #276749)',
                color: 'white', boxShadow: '0 4px 12px rgba(72,187,120,0.3)',
              }}>Reprendre</button>
              <button onClick={handleStop} disabled={loading} style={{
                padding: '12px 6px', borderRadius: 12, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', border: '2px solid #fc8181',
                background: 'white', color: '#9b2c2c',
              }}>Arrêter</button>
            </div>

            <button onClick={handleStop} style={{
              width: '100%', padding: '10px', borderRadius: 12, fontSize: 14, fontWeight: 500,
              cursor: 'pointer', border: '2px solid #e2e8f0',
              background: 'white', color: '#718096',
            }}>
              Reconfigurer
            </button>
          </>
        )}

        {message && (
          <div style={{
            marginTop: 20, padding: 16, borderRadius: 12, fontSize: 15, fontWeight: 600,
            textAlign: 'center', animation: 'slideIn 0.3s ease-out',
            ...(msgType === 'success' ? { background: '#c6f6d5', color: '#22543d', border: '2px solid #9ae6b4' }
              : msgType === 'error'   ? { background: '#fed7d7', color: '#742a2a', border: '2px solid #fc8181' }
              :                         { background: '#ebf8ff', color: '#2c5282', border: '2px solid #90cdf4' }),
          }}>
            {message}
          </div>
        )}

        <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#a0aec0', margin: 0 }}>WeStudy · Pomodoro Service</p>
        </div>

      </div>
    </div>
  );
}